<template>
  <!-- 活动区：zone=top 宫格上 | zone=bottom 宫格下（蛋糕/商城共用组件，layout 区分样式） -->
  <div
    v-if="dataList && dataList.length"
    class="activity-zone"
    :class="['activity-zone--' + zone, 'activity-zone--' + layout]"
  >
    <!-- 第二行 3+ 个：横向滚动 -->
    <div v-if="useScroll" class="scroll-outer" :class="{ 'scroll-outer--fade': showFade }">
      <div ref="scrollTrack" class="scroll-track" @scroll="onScroll">
        <div
          v-for="(item, index) in dataList"
          :key="index"
          class="activity-card activity-card--scroll"
          @click="onActivityClick(item)"
        >
          <div class="activity-card__viewport activity-card__viewport--half">
            <img
              v-if="isGifUrl(getImgUrl(item))"
              :src="getImgUrl(item)"
              alt=""
              class="img img--left-half"
            />
            <img
              v-else
              v-lazy="getImgUrl(item)"
              alt=""
              class="img img--left-half"
            />
          </div>
        </div>
      </div>
      <div v-if="showFade" class="scroll-hint" aria-hidden="true">
        <van-icon name="arrow" class="scroll-hint__icon" />
      </div>
    </div>
    <!-- 固定行：1 全宽 | 2 各 50% -->
    <div v-else class="activity-row" :class="'activity-row--' + count">
      <div
        v-for="(item, index) in dataList"
        :key="index"
        class="activity-card"
        @click="onActivityClick(item)"
      >
        <div
          class="activity-card__viewport"
          :class="useHalfViewport ? 'activity-card__viewport--half' : 'activity-card__viewport--full'"
        >
          <img
            v-if="isGifUrl(getImgUrl(item))"
            :src="getImgUrl(item)"
            alt=""
            :class="['img', { 'img--left-half': useHalfViewport }]"
          />
          <img
            v-else
            v-lazy="getImgUrl(item)"
            alt=""
            :class="['img', { 'img--left-half': useHalfViewport }]"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { queryAndNavigateHotShow, markHomeLeaveForH5 } from '@/utils/categoryNav';
import { isGifUrl } from '@/utils/image';
import { peekPageViewState } from '@/utils/pageScrollRestore';

export default {
  name: 'CakeLableImage',
  props: {
    dataList: {
      type: Array,
      default: () => [],
    },
    zone: {
      type: String,
      default: 'top',
      validator: (v) => ['top', 'bottom'].includes(v),
    },
    pageKey: {
      type: String,
      default: '',
    },
    sectionKey: {
      type: String,
      default: '',
    },
    /** mall=商城首页 C 区；cake=蛋糕首页活动条 */
    layout: {
      type: String,
      default: 'cake',
      validator: (v) => ['mall', 'cake'].includes(v),
    },
    formContext: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      showFade: false,
    };
  },
  computed: {
    count() {
      return (this.dataList || []).length;
    },
    /**
     * 宫格下 3+ 个横滑（蛋糕/商城一致）。
     * 蛋糕不做 3 等分铺满：槽宽 1/3 时无法精确展示源图左 50%，故 3 个也走横滑 + 50% 裁切。
     */
    useScroll() {
      return this.zone === 'bottom' && this.count >= 3;
    },
    /** 并排 2 个时走 half viewport，裁原图左 50% */
    useHalfViewport() {
      return this.count >= 2;
    },
  },
  watch: {
    dataList: {
      handler() {
        this.$nextTick(() => this.checkFade());
      },
      deep: true,
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.checkFade();
      this.tryRestoreScroll();
    });
  },
  activated() {
    this.tryRestoreScroll();
  },
  methods: {
    isGifUrl,
    getImgUrl(item) {
      return (item && item.showImgId && item.showImgId.showImg) || '';
    },
    checkFade() {
      const el = this.$refs.scrollTrack;
      if (!el) {
        this.showFade = false;
        return;
      }
      this.showFade = el.scrollWidth > el.clientWidth + 2;
    },
    onScroll() {
      const el = this.$refs.scrollTrack;
      if (!el) return;
      const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 4;
      this.showFade = !atEnd && el.scrollWidth > el.clientWidth + 2;
    },
    getScrollLeft() {
      return this.$refs.scrollTrack ? this.$refs.scrollTrack.scrollLeft : 0;
    },
    setScrollLeft(scrollLeft) {
      if (this.$refs.scrollTrack && scrollLeft != null) {
        this.$refs.scrollTrack.scrollLeft = scrollLeft;
        this.onScroll();
      }
    },
    tryRestoreScroll() {
      if (!this.pageKey || !this.sectionKey) return;
      const state = peekPageViewState(this.pageKey);
      if (!state || !state.pending) return;
      const left = state.sections && state.sections[this.sectionKey];
      if (left == null) return;
      this.$nextTick(() => {
        this.setScrollLeft(left);
        this.checkFade();
      });
    },
    onActivityClick(item) {
      queryAndNavigateHotShow(this, item, {
        hotType: 'B',
        listHotType: 'B',
        formOverrides: this.formContext,
        onBeforeH5Leave: () => this.saveHomeScrollBeforeLeave(),
      });
    },
    saveHomeScrollBeforeLeave() {
      let parent = this.$parent;
      while (parent) {
        if (parent.$refs && parent.$refs.category) {
          markHomeLeaveForH5(parent.$refs.category.current || 0);
          return;
        }
        parent = parent.$parent;
      }
      markHomeLeaveForH5(0);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/horizontalScrollHint.scss';

.activity-zone {
  width: 100%;
  box-sizing: border-box;

  /* ========== 商城首页 C 区样式 ========== */
  &--mall {
    margin-bottom: 0;

    .activity-row {
      gap: 0.5rem;
    }

    .activity-card {
      border-radius: 0.5rem;
      box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
    }

    .scroll-track {
      gap: 0.5rem;
    }

    /* 702×240 单张 | 360×240 并排/横滑 */
    .activity-card__viewport--full::before {
      padding-top: 34.188%;
    }

    .activity-card__viewport--half::before {
      padding-top: 66.667%;
    }
  }

  /* ========== 蛋糕首页：上下行高度一致，统一 702×150 / 360×150 + 左 50% 裁切 ========== */
  &--cake {
    margin-bottom: 0.4rem;

    .activity-row {
      gap: 0.35rem;
    }

    .activity-card {
      border-radius: 0.4rem;
      box-shadow: 0 1px 8px rgba(0, 0, 0, 0.04);
    }

    .scroll-track {
      gap: 0.35rem;
    }

    /*
     * 统一活动条高度（相对整行内容宽）= 并排半格高度 ≈ (行宽 - gap) / 2 × 150/360
     * 单张 / 并排 / 横滑视觉高度一致；横滑单卡宽 = 并排半格宽，保证左 50% 裁切。
     */
    $cake-activity-row-h: calc((100% - 0.35rem) * 0.2083335);

    .activity-card__viewport--full::before {
      padding-top: 20.833%;
    }

    .activity-card__viewport--half::before {
      padding-top: 41.667%;
    }

    .scroll-outer {
      &::before {
        content: '';
        display: block;
        width: 100%;
        padding-top: $cake-activity-row-h;
      }

      .scroll-track {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 100%;
      }

      .activity-card--scroll {
        /* 与并排半格同宽，才能精确裁源图左 50%（42% 槽宽只能看到约 42% 源图） */
        flex: 0 0 calc((100% - 0.35rem) / 2);
        height: 100%;

        .activity-card__viewport--half {
          height: 100%;

          &::before {
            display: none;
          }
        }
      }
    }
  }
}

.activity-row {
  display: flex;
  width: 100%;
  box-sizing: border-box;

  .activity-card {
    flex: 1;
    min-width: 0;
  }
}

.activity-card {
  overflow: hidden;
  background: #fff;
  box-sizing: border-box;

  &__viewport {
    position: relative;
    width: 100%;
    overflow: hidden;

    &::before {
      content: '';
      display: block;
      width: 100%;
    }
  }

  &__viewport--full .img:not(.img--left-half) {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  &__viewport--half .img--left-half {
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: auto;
    max-width: none;
    display: block;
  }

  &--scroll {
    flex: 0 0 42%;
    scroll-snap-align: start;
  }
}

.scroll-outer {
  position: relative;
  overflow: hidden;
  width: 100%;
  box-sizing: border-box;

  @include horizontal-scroll-hint(#f5f5f5);
}

.scroll-track {
  display: flex;
  gap: 0.35rem;
  align-items: stretch;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  scroll-snap-type: x mandatory;
  box-sizing: border-box;

  &::-webkit-scrollbar {
    display: none;
  }
}
</style>
